import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { setNewRelicCustomAttribute } from 'ddc-new-relic';
import { usePrefs, useLabels, useRequestData, useFlags } from 'wsm-common-data';
import { setClassNames } from 'ddc-classnames-js';
import {
	HighlightedAttribute,
	getFuelTypeIconAndPopoverLabel,
	LocationPopoverContent,
	isValidFuelType,
	isInTransit,
	getInTransitLabel
} from 'wsm-highlighted-attribute';
import { highlightedAttrList } from '../../../global-constants';
import { getAttributeValue } from '../../../utilities/highlightedAttributes';
import { selectIsGridLayout } from '../../../features/layoutSlice';
import { checkConcaternateAttr } from '../../../utilities/concatenateAttributeCheck';

const HighlightedAttributeContainer = ({ highlightedAttributes }) => {
	const isGridLayout = useSelector((state) => selectIsGridLayout(state));
	const { useSimplifiedListing } = useSelector((state) => state.layout);
	const {
		enableLocationHighlight,
		enableFuelTypeHighlight,
		highlightedFuelTypeElectricIcon,
		highlightedFuelTypeHybridIcon
	} = usePrefs();
	const labels = useLabels();
	const { deviceType, locale } = useRequestData();
	const flags = useFlags();
	const isMobile = deviceType === 'MOBILE';

	const useHighlightedAttrbuteIDC =
		flags['use-highlighted-attributes-from-idc'];

	const useSimplifiedHighlightedAttr =
		flags['use-simplified-highlighted-attribute'];

	const useHighlightedAttrOdometerFlag =
		flags['srp-show-odometer-as-highlighted-attribute'];

	let fuelTypeAttribute;
	const fuelType = getAttributeValue(
		highlightedAttributes,
		highlightedAttrList.FUEL_TYPE
	);
	if (
		(enableFuelTypeHighlight === 'true' || useHighlightedAttrbuteIDC) &&
		isValidFuelType(fuelType)
	) {
		const [fuelTypeIcon, fuelTypePopover] = getFuelTypeIconAndPopoverLabel({
			fuelType,
			electricIcon: highlightedFuelTypeElectricIcon,
			hybridIcon: highlightedFuelTypeHybridIcon,
			labels
		});
		setNewRelicCustomAttribute('fuel-highlighted-attribute', true);
		fuelTypeAttribute = (
			<HighlightedAttribute
				icon={fuelTypeIcon}
				popoverContent={fuelTypePopover}
				type="fuelType"
				chipClasses={[
					useSimplifiedHighlightedAttr
						? 'default mr-2'
						: `${fuelType.toLowerCase()} mr-3`,
					isGridLayout && !useSimplifiedListing ? 'mb-3' : '',
					useSimplifiedListing ? 'my-3' : ''
				]}
				isSimplified={useSimplifiedHighlightedAttr}
			>
				{fuelType}
			</HighlightedAttribute>
		);
	}

	let locationAttribute;
	const account = getAttributeValue(
		highlightedAttributes,
		highlightedAttrList.LOCATION
	);
	if (
		(enableLocationHighlight === 'true' || useHighlightedAttrbuteIDC) &&
		account?.name
	) {
		const cleanTitle = window?._?.unescape(account.name);
		const popoverAria = `${labels.get(
			'THIS_VEHICLE_IS_LOCATED_AT'
		)} ${cleanTitle}`;
		let popoverContent;
		if (Object.keys(account.address).length !== 0 || account.phone) {
			popoverContent = (
				<LocationPopoverContent
					phone={account.phone}
					id={account.id}
					address={account.address}
					isMobile={isMobile}
				/>
			);
		}
		locationAttribute = (
			<HighlightedAttribute
				icon="ddc-icon-map"
				popoverContent={popoverContent}
				type="location"
				chipClasses={[
					useSimplifiedHighlightedAttr
						? 'default mr-2'
						: 'location mr-3',
					useSimplifiedListing ? 'my-3' : '',
					isGridLayout && !useSimplifiedListing ? 'mb-3' : ''
				]}
				popoverAria={popoverAria}
				isSimplified={useSimplifiedHighlightedAttr}
			>
				{cleanTitle}
			</HighlightedAttribute>
		);
	}

	let inTransitAttribute;
	const inTransitData = getAttributeValue(
		highlightedAttributes,
		highlightedAttrList.ESTIMATED_DELIVERY_DATE
	);

	if (isInTransit(inTransitData?.status)) {
		const { estimatedDeliveryDate } = inTransitData;
		const inTransitText = getInTransitLabel({
			estimatedDeliveryDate,
			locale,
			labels
		});

		inTransitAttribute = (
			<HighlightedAttribute
				popoverContent={inTransitText}
				type="inTransit"
				chipClasses={[
					useSimplifiedHighlightedAttr ? 'default mr-2' : 'mr-3',
					useSimplifiedListing ? 'my-3' : '',
					isGridLayout && !useSimplifiedListing ? 'mb-3' : ''
				]}
				isSimplified={useSimplifiedHighlightedAttr}
			>
				{inTransitText}
			</HighlightedAttribute>
		);
	}

	let odometerAttribute;
	const odometer = getAttributeValue(
		highlightedAttributes,
		highlightedAttrList.ODOMETER
	);

	/**
	 * When vehicle don't have odometer or it's value is 0, the value is still return with odometer unit 'miles', 'km', etc.
	 * Need use regex to check and only display highlighted odometer have formated odometer value and odometer unit
	 * The value is valid if contains odometer value from 1 to 3 digits or if number have more than 3 digits,
	 * we have comma for each 3 degits group.
	 */
	const isValidOdometer = checkConcaternateAttr(
		highlightedAttrList.ODOMETER,
		odometer
	);

	if (
		useHighlightedAttrbuteIDC &&
		isValidOdometer &&
		useHighlightedAttrOdometerFlag
	) {
		const odometerPopoverContent = getAttributeValue(
			highlightedAttributes,
			highlightedAttrList.ODOMETER,
			true
		);
		odometerAttribute = (
			<HighlightedAttribute
				popoverContent={odometerPopoverContent}
				type="odometer"
				chipClasses={[
					useSimplifiedHighlightedAttr ? 'default mr-2' : 'mr-3',
					useSimplifiedListing ? 'my-3' : '',
					isGridLayout && !useSimplifiedListing ? 'mb-3' : ''
				]}
				isSimplified={useSimplifiedHighlightedAttr}
			>
				{odometer}
			</HighlightedAttribute>
		);
	}

	const isEnabled = [
		inTransitAttribute,
		locationAttribute,
		fuelTypeAttribute,
		odometerAttribute
	].some((attr) => !!attr);

	if (isEnabled) {
		return (
			<div
				className={setClassNames([
					'vehicle-card-highlight',
					useSimplifiedListing ? 'mb-0' : 'mb-3',
					'align-self-start',
					'd-flex',
					'flex-wrap'
				])}
			>
				{inTransitAttribute}
				{locationAttribute}
				{fuelTypeAttribute}
				{odometerAttribute}
			</div>
		);
	}
	return null;
};

HighlightedAttributeContainer.propTypes = {
	highlightedAttributes: PropTypes.PropTypes.arrayOf(PropTypes.shape({}))
};

export default HighlightedAttributeContainer;
